// src/services/audioApiService.ts
import axios from "axios";

export interface Reciter {
  id: string;
  name: string;
  nameArabic?: string;
}

export interface AudioSegments {
  ayah: number;
  audioUrl: string;
  segments: Array<{
    startTime: number;
    endTime: number;
    wordIndex: number;
  }>;
}

const api = axios.create({
  baseURL: "https://api.quran.com/api/v4/",
});

class AudioApiService {
  // =======================
  // 1) Get reciters list
  // =======================
  async getReciters(): Promise<Reciter[]> {
    const res = await api.get("/resources/recitations");

    return res.data.recitations.map((r: any) => ({
      id: r.id.toString(), // Convert to string for consistency
      name: r.translated_name?.name || r.style || r.reciter_name,
      nameArabic: r.reciter_name,
    }));
  }

  // ===================================
  // 2) Get audio for specific ayah
  // ===================================
  async getAyahAudio(
    chapter: number,
    ayah: number,
    reciterId: number | string
  ): Promise<AudioSegments> {
    const reciterIdNum = typeof reciterId === 'string' ? parseInt(reciterId, 10) : reciterId;
    
    if (isNaN(reciterIdNum)) {
      throw new Error(`Invalid reciter ID: ${reciterId}`);
    }

    const res = await api.get("quran/verses/audio", {
      params: {
        recitation: reciterIdNum,
        chapter_number: chapter,
        fields: "timestamps",
        segments: true,
      },
    });

    const verses = res.data.verses;
    const verse = verses.find((v: any) => v.verse_key.endsWith(":" + ayah));

    if (!verse) throw new Error("Ayah audio not found");

    return {
      ayah,
      audioUrl: verse.audio_url || verse.audioUrl || '',
      segments: (verse.timestamps || []).map((t: any, i: number) => ({
        startTime: t.timestamp_from || t.startTime || 0,
        endTime: t.timestamp_to || t.endTime || 0,
        wordIndex: i,
      })),
    };
  }

  // ===================================
  // 3) Full surah audio
  // Try API first, then fallback to direct URL pattern
  // ===================================
  async getSurahAudio(surah: number, reciterId?: number | string) {
    // Use default reciter ID 1 if not provided
    const reciterIdNum = reciterId 
      ? (typeof reciterId === 'string' ? parseInt(reciterId, 10) : reciterId)
      : 1;
    
    if (isNaN(reciterIdNum)) {
      throw new Error(`Invalid reciter ID: ${reciterId}`);
    }

    // Try to get audio URL from API first
    try {
      const res = await api.get(`/chapter_recitations/${reciterIdNum}/${surah}`);
      
      console.log('Surah audio response from API:', res.data);
      
      const audioUrl = res.data.audio_file?.audio_url || 
                       res.data.audio_file?.url ||
                       res.data.audio_url ||
                       '';

      if (audioUrl && audioUrl.trim() !== '') {
        console.log('Using API audio URL:', audioUrl, 'for reciter ID:', reciterIdNum);
        return {
          ayah: 1,
          audioUrl: audioUrl,
          segments: [],
        };
      }
    } catch (apiError: any) {
      console.warn('API request failed, using fallback URL pattern:', apiError?.response?.data || apiError.message);
    }

    // Fallback to direct URL pattern
    const fallbackUrl = `https://download.quranicaudio.com/qdc/abdul_baset/mujawwad/${surah}.mp3`;
    console.log('Using fallback audio URL:', fallbackUrl, 'for reciter ID:', reciterIdNum);

    return {
      ayah: 1,
      audioUrl: fallbackUrl,
      segments: [],
    };
  }
}

export const audioApi = new AudioApiService();
